import { Builder } from "./builder.js";
import { SPAWN, gradeFor, groundY } from "./spec.js";

import * as exterior from "./exterior/index.js";

/**
 * Assembles the neighbourhood.
 *
 * Order matters only for ground surfaces stacking on each other; every module
 * is otherwise independent and reads its coordinates from spec.js, which is
 * what let them be written in parallel.
 */
const MODULES = [["exterior", exterior]];

/**
 * How far above the bare earth a covering may be and still count as the floor.
 *
 * Paving, sod and tarmac are a few centimetres and a garden edging course is
 * 0.10, so 0.12 clears every real covering in the level.  A deck at 1.35 or a
 * first floor at 0.55 are emphatically NOT ground coverings, and they already
 * carry real colliders -- the band is what keeps surfaceY from quietly
 * lifting the cat onto the kitchen floor as it walks past the house.
 *
 * It is deliberately tighter than it needs to be for another reason.  A flat
 * decal laid on a slope floats at its downhill edge -- the greenspace's soil
 * patches reach 0.20 above the ground on the creek banks -- and standing the
 * cat on that edge would be standing it on a plate half in the air.  Below
 * the band those become sinking again, which is the lesser of the two.
 */
const SURFACE_BAND = 0.12;
const SURFACE_CELL = 2;
const skey = (i, j) => (i + 16384) * 32768 + (j + 16384);

/**
 * The height the cat actually stands at: the ground, or whatever was laid on
 * top of it.
 *
 * groundY is the earth.  Almost nowhere in this level does the player touch
 * the earth -- they touch paving, tarmac, flagstone, a sod lawn, all of which
 * are drawn proud of it and none of which carry colliders, because they are
 * two centimetres thick and there are several hundred of them.  Flooring on
 * the earth therefore sank the cat's paws into every paved surface in the
 * level by 3-6 cm.
 *
 * Bucketed by a 2 m cell so a query touches a handful of rectangles rather
 * than all four hundred; the player asks once a frame, but reach.mjs asks
 * half a million times and notices.
 */
export function surfaceLookup(surfaces) {
  const cells = new Map();
  for (const s of surfaces) {
    const i0 = Math.floor(s.x0 / SURFACE_CELL), i1 = Math.floor(s.x1 / SURFACE_CELL);
    const j0 = Math.floor(s.z0 / SURFACE_CELL), j1 = Math.floor(s.z1 / SURFACE_CELL);
    for (let i = i0; i <= i1; i++) {
      for (let j = j0; j <= j1; j++) {
        const k = skey(i, j);
        let bucket = cells.get(k);
        if (!bucket) cells.set(k, (bucket = []));
        bucket.push(s);
      }
    }
  }
  return (x, z) => {
    const g = groundY(x, z);
    const bucket = cells.get(skey(Math.floor(x / SURFACE_CELL),
                                  Math.floor(z / SURFACE_CELL)));
    if (!bucket) return g;
    let top = g;
    for (const s of bucket) {
      if (x < s.x0 || x > s.x1 || z < s.z0 || z > s.z1) continue;   // cheap reject
      if (s.hw !== undefined) {
        // oriented: rotate the point into the decal's own frame
        const dx = x - s.cx, dz = z - s.cz;
        if (Math.abs(dx * s.cos - dz * s.sin) > s.hw) continue;
        if (Math.abs(dx * s.sin + dz * s.cos) > s.hd) continue;
      }
      if (s.top > top && s.top - g <= SURFACE_BAND) top = s.top;
    }
    return top;
  };
}

export function buildLevel(scene) {
  const b = new Builder();
  const report = [];

  for (const [name, mod] of MODULES) {
    const build = mod.build ?? mod.default;
    if (typeof build !== "function") {
      console.warn(`level: ${name} has no build()`);
      continue;
    }
    b.setOwner(name);
    b.setGrade(gradeFor(name));       // which terrace it builds on
    const beforeTris = b.stats().triangles;
    const beforeCols = b.colliders.length;
    try {
      build(b);
    } catch (e) {
      // One bad module should not cost the player the whole street.
      console.error(`level: ${name} failed --`, e);
      continue;
    }
    report.push({
      name,
      triangles: b.stats().triangles - beforeTris,
      colliders: b.colliders.length - beforeCols,
    });
  }

  const merged = b.finish(scene);
  return {
    ...merged,
    surfaceY: surfaceLookup(b.surfaces),
    colliders: b.colliders,
    pieces: b.pieces,
    markers: b.markers,
    spawn: b.markers.spawn ?? SPAWN,
    report,
  };
}

export { SPAWN };
